import React, { useEffect, useRef } from "react";
import styled from "styled-components";

// ─── Styled components ───
// Matches existing ConfirmDialog pattern (ConfirmDialog.tsx lines 17-28)
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* z-index hierarchy: SessionWarningModal (9999) < SessionExpiredModal (10000) = ConfirmDialog (10000) */
  z-index: 10000;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 480px;
  width: 90%;
  padding: 32px;
  text-align: center;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  font-size: 20px;
  color: #1a1a1a;
`;

const Message = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  background-color: #0047ab;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #003580;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: #0047ab;
  border: 1px solid #0047ab;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f0f4ff;
  }
`;

interface SessionExpiredModalProps {
  wasMagicLinkUser: boolean;
  onRequestNewLink: () => void;
  onReLogin: () => void;
}

const SessionExpiredModal: React.FC<SessionExpiredModalProps> = ({
  wasMagicLinkUser,
  onRequestNewLink,
  onReLogin,
}) => {
  const primaryRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the primary action button for accessibility
  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <ModalBox>
        <Title id="session-expired-title">Your session has expired</Title>
        <Message>
          For your security, sessions expire after 30 minutes. Please sign in
          again to continue.
        </Message>
        <ButtonGroup>
          {wasMagicLinkUser ? (
            <>
              <PrimaryButton ref={primaryRef} onClick={onRequestNewLink}>
                Request New Link
              </PrimaryButton>
              <SecondaryButton onClick={onReLogin}>
                Log In Again
              </SecondaryButton>
            </>
          ) : (
            <PrimaryButton ref={primaryRef} onClick={onReLogin}>
              Sign In Again
            </PrimaryButton>
          )}
        </ButtonGroup>
      </ModalBox>
    </Overlay>
  );
};

export default SessionExpiredModal;
